import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Reveal } from '../../shared/ui';
import { usePageMeta } from '../../shared/hooks/usePageMeta';
import { PARISHES_SERVED, JOBS_COMPLETED, QUOTE_TURNAROUND, OPERATING_SINCE } from '../../shared/constants/business';

const STATS = [
  { value: JOBS_COMPLETED, label: 'Jobs completed' },
  { value: PARISHES_SERVED, label: 'Parishes served' },
  { value: QUOTE_TURNAROUND, label: 'Quote turnaround' },
  { value: OPERATING_SINCE, label: 'Operating since' },
];

const VALUES = [
  {
    title: 'Safety first',
    body: 'Every driver is licensed, vetted, and trained. Equipment is inspected before every job, no exceptions.',
  },
  {
    title: 'Reliability',
    body: 'We show up on time, every time. Real-time tracking so your site team knows exactly where the equipment is.',
  },
  {
    title: 'Transparency',
    body: 'Full cost breakdown before you commit. No hidden fees, no surprises on the final invoice.',
  },
];

const heading = 'm-0 font-bold leading-[1.05] tracking-[-0.04em]';

export function AboutPage() {
  usePageMeta('About', "Learn about RT Equipment & Haulage — Jamaica's heavy equipment transport company.");
  return (
    <div className="overflow-x-clip bg-black text-[#f5f5f7]">
      {/* Hero */}
      <section className="mx-auto max-w-[1200px] px-6 pt-[clamp(48px,9vw,88px)]">
        <Reveal immediate className="mb-2.5 text-[21px] font-semibold tracking-[-0.015em] text-accent">
          Kingston, Jamaica
        </Reveal>
        <Reveal as="h1" kind="blur" immediate delay={100} className="m-0 max-w-[980px] text-[clamp(44px,8vw,96px)] font-bold leading-[1.02] tracking-[-0.045em] [text-wrap:balance]">
          Built for the <span className="text-[#6e6e73]">people who</span> build Jamaica.
        </Reveal>
        <Reveal as="p" immediate delay={400} className="mt-5 max-w-[640px] text-[clamp(19px,2.2vw,24px)] leading-[1.35] tracking-[-0.015em] text-[#a1a1a6] [text-wrap:balance]">
          RT Equipment &amp; Haulage is Jamaica's dedicated platform for heavy equipment transport and haulage, built for
          contractors, site operators, and construction companies who need reliable, transparent logistics.
        </Reveal>
      </section>

      {/* Story + stats */}
      <section className="mx-auto grid max-w-[1200px] items-start gap-5 px-[22px] pt-[clamp(72px,10vw,120px)] [grid-template-columns:repeat(auto-fit,minmax(min(100%,420px),1fr))]">
        <Reveal className="rounded-[32px] bg-[#161617] p-[clamp(28px,5vw,56px)]">
          <h2 className={`${heading} mb-6 text-[clamp(32px,4vw,44px)]`}>Our story</h2>
          <div className="flex flex-col gap-4 text-[17px] leading-[1.65] text-[#c7c7cc]">
            <p className="m-0">
              Coordinating heavy equipment moves across Jamaica's parishes was slow, expensive, and unreliable.
              Contractors spent hours on the phone chasing quotes, drivers had no way to confirm routes, and invoices
              were full of surprises.
            </p>
            <p className="m-0">
              Founded in 2018, RT Equipment &amp; Haulage has been serving contractors, site operators, and construction
              companies across Jamaica ever since.
            </p>
            <p className="m-0">
              We built RT Equipment &amp; Haulage to fix that: a platform that digitises the entire workflow from quote
              to delivery. Real-time tracking, JMD pricing with full breakdowns, and a managed fleet operating out of our
              Kingston yard.
            </p>
          </div>
        </Reveal>

        <Reveal kind="stagger" className="grid grid-cols-2 gap-5">
          {STATS.map(({ value, label }) => (
            <div key={label} className="rounded-[28px] bg-[#161617] p-[clamp(20px,4vw,36px)]">
              <p className="m-0 mb-1 text-[clamp(36px,5vw,56px)] font-bold leading-none tracking-[-0.04em] text-accent">{value}</p>
              <p className="m-0 text-[15px] text-[#a1a1a6]">{label}</p>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-[1200px] px-[22px] pt-[clamp(96px,14vw,160px)]">
        <Reveal as="h2" className={`${heading} mb-[clamp(36px,6vw,64px)] text-center text-[clamp(40px,5.6vw,64px)]`}>
          What we <span className="text-[#6e6e73]">stand for.</span>
        </Reveal>
        <Reveal kind="stagger" className="grid gap-[clamp(32px,5vw,48px)] [grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr))]">
          {VALUES.map(({ title, body }, i) => (
            <div key={title} className="flex flex-col gap-3 border-t-2 border-[#333336] pt-7">
              <div className="text-[56px] font-bold leading-none tracking-[-0.04em] text-accent">{i + 1}</div>
              <div className="text-2xl font-semibold tracking-[-0.02em]">{title}</div>
              <div className="max-w-[340px] text-[#a1a1a6]">{body}</div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* CTA */}
      <section className="flex flex-col items-center gap-7 px-[22px] pb-[clamp(80px,12vw,140px)] pt-[clamp(96px,14vw,160px)] text-center">
        <Reveal as="h2" kind="blur" className="m-0 max-w-[860px] text-[clamp(44px,7vw,80px)] font-bold leading-[1.02] tracking-[-0.045em] [text-wrap:balance]">
          Ready to work with us?
        </Reveal>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/signup"
            className="mk-pulse inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-[19px] font-medium text-black transition-all duration-300 hover:scale-[1.04] hover:bg-[#ffb84a] hover:shadow-[0_10px_30px_rgba(245,165,36,0.35)] active:scale-95"
          >
            Request a haul <ArrowRight size={18} strokeWidth={2.5} />
          </Link>
          <Link to="/#contact" className="inline-flex items-center gap-0.5 px-2 py-3 text-[17px] text-accent hover:underline">
            Contact us <ChevronRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
